import React, { useState, useEffect } from 'react';
import { 
  Box, Button,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Typography, IconButton, Tooltip
} from '@mui/material';
import { WorkoutSet, Exercise } from '../../types';
import { WorkoutEntryDialog, WorkoutEntryFormState } from './WorkoutEntryDialog';
import { QuickAddSetDialog } from './QuickAddSetDialog';
import { WorkoutSetItem } from '../common/WorkoutSetItem';
import { useLanguage } from '../../i18n/LanguageContext';
import { formatWorkoutsForSharing } from '../../utils/share';
import { commonDialogStyles } from '../common/dialogStyles';
import { Share as ShareIcon } from '@mui/icons-material';

interface WorkoutListProps {
  workouts: WorkoutSet[];
  workoutLoading: boolean;
  workoutError: string | null;
  currentSetForDialog: WorkoutEntryFormState | null;
  setCurrentSetForDialog: (set: WorkoutEntryFormState | null) => void;
  handleEditSetTrigger: (set: WorkoutSet) => void;
  handleSaveEditSet: (setId: number | undefined, formData: WorkoutEntryFormState) => void;
  handleCancelEditSet: () => void;
  handleOpenDeleteDialog: (setId: number | undefined) => void;
  deleteDialogOpen: boolean;
  handleCancelDelete: () => void;
  handleConfirmDelete: () => void;
  selectedDate: string;
  exercises: Exercise[];
  isSavingSet: boolean;
  handleDeleteWorkoutDetail: (detailId: number | undefined) => Promise<void>;
  onCreateExercise?: (name: string) => Promise<Exercise | null>;
  onQuickAddSet?: (workoutId: number, reps: string, weight: string, series: number) => Promise<void>;
  onQuickEditSet?: (workoutId: number, setDetail: any, reps: string, weight: string, series: number) => Promise<void>;
}

const defaultWorkoutFormState: WorkoutEntryFormState = {
  exercise: '',
  comment: '',
  details: [{ series: '', reps: '', weight: '', rest_time_seconds: '', rir: '' }],
  workout_type: 'traditional',
  amrap_time_minutes: '',
  amrap_details: []
};

export const WorkoutList: React.FC<WorkoutListProps> = ({
  workouts,
  workoutLoading,
  workoutError,
  currentSetForDialog,
  setCurrentSetForDialog,
  handleEditSetTrigger,
  handleSaveEditSet,
  handleCancelEditSet,
  handleOpenDeleteDialog,
  deleteDialogOpen,
  handleCancelDelete,
  handleConfirmDelete,
  selectedDate,
  exercises,
  isSavingSet,
  handleDeleteWorkoutDetail,
  onCreateExercise,
  onQuickAddSet,
  onQuickEditSet
}) => {
  const { t } = useLanguage();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentEditingSetId, setCurrentEditingSetId] = useState<number | null>(null);
  const [editDialogFormState, setEditDialogFormState] = useState<WorkoutEntryFormState>(defaultWorkoutFormState);
  const [expandedItems, setExpandedItems] = useState<{ [key: number]: boolean }>({});
  
  // Quick add set dialog state
  const [quickAddDialogOpen, setQuickAddDialogOpen] = useState(false);
  const [currentQuickAddWorkoutId, setCurrentQuickAddWorkoutId] = useState<number | null>(null);
  const [isQuickAddSaving, setIsQuickAddSaving] = useState(false);
  const [quickAddInitialValues, setQuickAddInitialValues] = useState<any>(null);
  
  // Quick edit set dialog state  
  const [quickEditDialogOpen, setQuickEditDialogOpen] = useState(false);
  const [currentEditSetDetail, setCurrentEditSetDetail] = useState<any>(null);
  const [currentEditWorkoutId, setCurrentEditWorkoutId] = useState<number | null>(null);
  const [isQuickEditSaving, setIsQuickEditSaving] = useState(false);

  useEffect(() => {
    const initialExpandedState = workouts.reduce((acc, _, index) => {
      acc[index] = true;
      return acc;
    }, {} as { [key: number]: boolean });
    setExpandedItems(initialExpandedState);
  }, [workouts]);

  useEffect(() => {
    if (currentSetForDialog && currentEditingSetId !== null) {
      setEditDialogFormState(currentSetForDialog);
    } else {
      setEditDialogFormState(defaultWorkoutFormState);
    }
  }, [currentSetForDialog, currentEditingSetId]);

  const handleToggleExpand = (idx: number) => {
    setExpandedItems(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  const handleOpenEditDialogLocal = (set: WorkoutSet) => {
    handleEditSetTrigger(set);
    // For AMRAP workouts, use a special identifier since they don't have individual IDs
    setCurrentEditingSetId(set.id ?? (set.workout_type === 'amrap' ? -1 : null));
    setEditDialogOpen(true);
  };
  
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setCurrentSetForDialog(null);
    setCurrentEditingSetId(null);
    handleCancelEditSet();
  };

  const handleEditDialogFormChange = (field: keyof WorkoutEntryFormState, value: any) => {
    const newFormState = { ...editDialogFormState, [field]: value };
    setEditDialogFormState(newFormState);
    setCurrentSetForDialog(newFormState);
  };

  const handleEditDialogSubmit = () => {
    if (currentEditingSetId !== null) {
      // For AMRAP workouts, pass undefined since they don't have individual IDs
      const setId = currentEditingSetId === -1 ? undefined : currentEditingSetId;
      handleSaveEditSet(setId, editDialogFormState);
    }
    handleCloseEditDialog();
  };

  // Quick add set handlers
  const handleOpenQuickAddDialog = (workoutId: number | undefined) => {
    if (workoutId) {
      setCurrentQuickAddWorkoutId(workoutId);
      
      // Find the workout and get values from the latest set
      const workout = workouts.find(w => w.id === workoutId);
      if (workout && workout.details.length > 0) {
        // Find the set with the highest series number (latest set)
        const latestSet = workout.details.reduce((latest, current) => 
          current.series > latest.series ? current : latest
        );
        
        setQuickAddInitialValues({
          reps: latestSet.reps?.toString() || '',
          weight: latestSet.weight?.toString() || '',
          series: '1', // Default to series 1
        });
      } else {
        // No existing sets, use empty defaults
        setQuickAddInitialValues({
          reps: '',
          weight: '',
          series: '1',
        });
      }
      
      setQuickAddDialogOpen(true);
    }
  };

  const handleCloseQuickAddDialog = () => {
    setQuickAddDialogOpen(false);
    setCurrentQuickAddWorkoutId(null);
    setIsQuickAddSaving(false);
    setQuickAddInitialValues(null);
  };

  const handleQuickAddSubmit = async (reps: string, weight: string, series: number) => {
    if (!currentQuickAddWorkoutId || !onQuickAddSet) {
      return;
    }

    setIsQuickAddSaving(true);
    try {
      await onQuickAddSet(currentQuickAddWorkoutId, reps, weight, series);
      handleCloseQuickAddDialog();
    } catch (error) {
      console.error('Failed to add set:', error);
      setIsQuickAddSaving(false);
    }
  };

  // Quick edit set handlers
  const handleOpenQuickEditDialog = (workoutId: number, setDetail: any) => {
    setCurrentEditWorkoutId(workoutId);
    setCurrentEditSetDetail(setDetail);
    setQuickEditDialogOpen(true);
  };

  const handleCloseQuickEditDialog = () => {
    setQuickEditDialogOpen(false);
    setCurrentEditSetDetail(null);
    setCurrentEditWorkoutId(null);
    setIsQuickEditSaving(false);
  };

  const handleQuickEditSubmit = async (reps: string, weight: string, series: number) => {
    if (!currentEditWorkoutId || !currentEditSetDetail || !onQuickEditSet) {
      return;
    }

    setIsQuickEditSaving(true);
    try {
      await onQuickEditSet(currentEditWorkoutId, currentEditSetDetail, reps, weight, series);
      handleCloseQuickEditDialog();
    } catch (error) {
      console.error('Failed to edit set:', error);
      setIsQuickEditSaving(false);
    }
  };

  if (workoutLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography>{t('workout.loading')}</Typography>
      </Box>
    );
  }

  if (workoutError) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="error">{workoutError}</Typography>
      </Box>
    );
  }

  if (workouts.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">{t('workout.noWorkouts')}</Typography>
      </Box>
    );
  }

  const handleShare = async () => {
    if (!navigator.share) {
      // Fallback: copy to clipboard
      const shareText = formatWorkoutsForSharing(workouts, selectedDate, t);
      try {
        await navigator.clipboard.writeText(shareText);
        // You might want to show a success message here
        console.log('Workout copied to clipboard');
      } catch (err) {
        console.error('Failed to copy to clipboard', err);
      }
      return;
    }

    const shareData = {
      title: t('workout.shareTitle'),
      text: formatWorkoutsForSharing(workouts, selectedDate, t),
    };
    try {
      await navigator.share(shareData);
    } catch (err) {
      console.error('Error sharing workout data', err);
    }
  };

  return (
    <>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Tooltip title={t('workout.share')}>
          <IconButton color="primary" onClick={handleShare} aria-label={t('workout.share')}>
            <ShareIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ mt: 2 }}>
        {workouts.map((set, idx) => (
          <WorkoutSetItem
            key={set.id || idx}
            workout={set}
            index={idx}
            expanded={expandedItems[idx] || false}
            onToggleExpand={handleToggleExpand}
            onEdit={handleOpenEditDialogLocal}
            onDelete={handleOpenDeleteDialog}
            onDeleteDetail={handleDeleteWorkoutDetail}
            onQuickAddSet={onQuickAddSet ? handleOpenQuickAddDialog : undefined}
            onQuickEditSet={onQuickEditSet ? handleOpenQuickEditDialog : undefined}
          />
        ))}
      </Box>
      
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete} sx={commonDialogStyles}>
        <DialogTitle>{t('workout.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('workout.deleteConfirmMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">{t('workout.cancel')}</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">{t('workout.delete')}</Button>
        </DialogActions>
      </Dialog>

      {editDialogOpen && (
        <WorkoutEntryDialog
          open={editDialogOpen}
          onClose={handleCloseEditDialog}
          mode={'edit'}
          exercises={exercises}
          initialFormValues={editDialogFormState}
          onFormChange={handleEditDialogFormChange}
          onSubmit={handleEditDialogSubmit}
          isSavingSet={isSavingSet}
          onCreateExercise={onCreateExercise}
        />
      )}

      <QuickAddSetDialog
        open={quickAddDialogOpen}
        onClose={handleCloseQuickAddDialog}
        onAdd={handleQuickAddSubmit}
        isSaving={isQuickAddSaving}
        mode="add"
        initialValues={quickAddInitialValues}
      />

      <QuickAddSetDialog
        open={quickEditDialogOpen}
        onClose={handleCloseQuickEditDialog}
        onAdd={handleQuickEditSubmit}
        isSaving={isQuickEditSaving}
        mode="edit"
        initialValues={currentEditSetDetail ? {
          reps: currentEditSetDetail.reps?.toString() || '',
          weight: currentEditSetDetail.weight?.toString() || '',
          series: currentEditSetDetail.series?.toString() || '1',
        } : undefined}
      />
    </>
  );
}; 